import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Mail, Lock, User, MapPin, Phone, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login"); // login or register
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    address: "",
    phone: "",
    promo_code: "",
    marketing_consent: false
  });

  // Check if already logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/customer/auth/me`, {
          withCredentials: true
        });
        if (response.data) {
          navigate("/mon-compte");
        }
      } catch (error) {
        // Not logged in, stay on page
      }
    };
    
    // Don't check if we're processing a callback
    if (!location.hash?.includes('session_id=')) {
      checkAuth();
    }
  }, [navigate, location]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = mode === "login" ? "/customer/auth/login" : "/customer/auth/register";
      const data = mode === "login" 
        ? { email: formData.email, password: formData.password }
        : formData;
      
      const response = await axios.post(`${API}${endpoint}`, data, {
        withCredentials: true
      });
      
      // Check if verification is required
      if (response.data.requires_verification) {
        setVerificationSent(true);
        toast.success(response.data.message);
      } else {
        // Track successful client login
        if (mode === "login") {
          // Save customer email for chatbot
          const customer = response.data.customer;
          if (customer?.email) {
            localStorage.setItem("customer_email", customer.email);
          }
          
          try {
            let visitorId = localStorage.getItem("sakura_visitor_id");
            if (!visitorId) {
              visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              localStorage.setItem("sakura_visitor_id", visitorId);
            }
            await axios.post(`${API}/track-visit`, { 
              page: "client_login", 
              visitor_id: visitorId,
              customer_id: customer?.id,
              customer_name: customer?.name,
              customer_email: customer?.email
            }, { withCredentials: true });
            console.log("Client login tracked successfully");
          } catch (trackError) {
            console.error("Track visit error:", trackError);
            // Silent fail - don't block login
          }
        }
        toast.success(response.data.message);
        navigate("/mon-compte");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Header */}
      <nav className="sticky top-0 bg-white/80 backdrop-blur-md z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-5 h-5" />
            Retour
          </button>
          <img 
            src="https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png" 
            alt="Pressing Sakura" 
            className="h-12 w-auto cursor-pointer"
            onClick={() => navigate("/")}
          />
          <div className="w-20"></div>
        </div>
      </nav>

      <div className="max-w-md mx-auto px-4 py-12">
        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {mode === "login" ? "Connexion" : "Créer un compte"}
            </CardTitle>
            <CardDescription className="text-base">
              {mode === "login" 
                ? "Accède à ton espace client" 
                : "Rejoins le programme fidélité Sakura"}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Verification sent message */}
            {verificationSent && (
              <div className="mb-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-bold text-green-800 text-lg mb-2">Vérifie ta boîte mail !</h3>
                <p className="text-green-700 text-sm mb-4">
                  Un email de confirmation a été envoyé à <strong>{formData.email}</strong>
                </p>
                <p className="text-green-600 text-xs">
                  Clique sur le lien dans l'email pour activer ton compte.<br/>
                  Pense à vérifier tes spams !
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => {
                    setVerificationSent(false);
                    setMode("login");
                  }}
                >
                  Retour à la connexion
                </Button>
              </div>
            )}
            
            {!verificationSent && (
              <>
                {/* Benefits banner for register */}
                {mode === "register" && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl border border-pink-100">
                    <h3 className="font-semibold text-pink-800 mb-2">✨ Avantages de ton compte</h3>
                    <ul className="text-sm text-pink-700 space-y-1">
                      <li>• Suis ta fidélité : 5€ offerts tous les 50€</li>
                      <li>• Accède à ton historique de commandes</li>
                      <li>• Réserve plus rapidement</li>
                      <li>• Reçois des offres exclusives</li>
                    </ul>
                  </div>
                )}

                {/* Google Login Button */}
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full mb-4 h-12 text-base"
                  onClick={handleGoogleLogin}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continuer avec Google
                </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">ou par email</span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "register" && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">Prénom</Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="first_name"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          placeholder="Prénom"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="last_name">Nom</Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="last_name"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          placeholder="Nom"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="address">Adresse</Label>
                    <div className="relative mt-1">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Ton adresse complète"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Téléphone (optionnel)</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="06 12 34 56 78"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div className="p-3 bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg" data-testid="promo-code-section">
                    <Label htmlFor="promo_code" className="text-pink-700 font-medium">🎁 Code promo ou parrainage (optionnel)</Label>
                    <div className="relative mt-1">
                      <Input
                        id="promo_code"
                        name="promo_code"
                        value={formData.promo_code}
                        onChange={(e) => setFormData({ ...formData, promo_code: e.target.value.toUpperCase() })}
                        placeholder="CODE ou PAR-XXXXX"
                        className="uppercase bg-white"
                        data-testid="promo-code-input"
                      />
                    </div>
                    <p className="text-xs text-pink-600 mt-1">Code promo ou code parrainage d'un ami = 5€ offerts !</p>
                  </div>
                </>
              )}
              
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="ton@email.com"
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Forgot password link - only in login mode */}
              {mode === "login" && (
                <div className="text-right">
                  <a 
                    href="/reset-password"
                    className="text-sm text-pink-600 hover:text-pink-700 hover:underline"
                  >
                    Mot de passe oublié ?
                  </a>
                </div>
              )}

              {/* Marketing consent - only in register mode */}
              {mode === "register" && (
                <div className="flex items-start gap-3 p-3 bg-pink-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="marketing_consent"
                    checked={formData.marketing_consent}
                    onChange={(e) => setFormData({ ...formData, marketing_consent: e.target.checked })}
                    className="mt-1 rounded border-pink-300 text-pink-600 focus:ring-pink-500"
                    data-testid="marketing-consent-checkbox"
                  />
                  <label htmlFor="marketing_consent" className="text-sm text-slate-600 cursor-pointer">
                    J'accepte de recevoir les offres et actualités de Pressing Sakura par email
                  </label>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full h-12 text-base bg-primary hover:bg-primary/90"
                disabled={loading}
              >
                {loading ? "Chargement..." : (mode === "login" ? "Se connecter" : "Créer mon compte")}
              </Button>
            </form>

            {/* Toggle mode */}
            <div className="mt-6 text-center text-sm">
              {mode === "login" ? (
                <p className="text-slate-600">
                  Pas encore de compte ?{" "}
                  <button 
                    onClick={() => setMode("register")}
                    className="text-primary font-semibold hover:underline"
                  >
                    Créer un compte
                  </button>
                </p>
              ) : (
                <p className="text-slate-600">
                  Déjà un compte ?{" "}
                  <button 
                    onClick={() => setMode("login")}
                    className="text-primary font-semibold hover:underline"
                  >
                    Se connecter
                  </button>
                </p>
              )}
            </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerAuth;
